import React, {
  ComponentProps,
  forwardRef,
  memo,
  ReactNode,
  useCallback,
  useRef,
  MouseEvent,
} from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { isFunction } from '@surglogs/util.is-function'

import type { SvgIcon } from '@surglogs-app/icons'
import { PropsWithTheme } from '@surglogs-app/ui.theme'

import { getDefaultOutline } from '../../mixins'
import {
  ButtonColors,
  ButtonShape,
  ButtonSize,
  ButtonVariant,
} from '../services/ButtonService'
import {
  StyleProps,
  getCSSObjectFromStyleProps,
} from '../styleProps/StylePropsService'

interface ButtonStyleProps extends StyleProps {
  variant: ButtonVariant
  size: ButtonSize
  disabled?: boolean
  shape?: ButtonShape
}

const getShapeStyles = ({ shape }: PropsWithTheme<ButtonStyleProps>) => {
  if (shape === 'square') {
    return css`
      width: var(--size);
      padding: 0;
    `
  }

  if (shape === 'rounded') {
    return css`
      border-radius: var(--size);
    `
  }

  return css``
}

const getSizeStyles = ({ theme, size }: PropsWithTheme<ButtonStyleProps>) => {
  if (size === 'mini') {
    return css`
      --size: ${theme.ui.spacings.xl};
      font-size: ${theme.ui.typography.fontSizes.xxs};
      line-height: ${theme.ui.typography.lineHeights.xxs};
      column-gap: ${theme.ui.spacings.xs};
      padding: 0 ${theme.ui.spacings['abs.12']};
    `
  }

  return css`
    font-size: ${theme.ui.typography.fontSizes.sm};
    line-height: ${theme.ui.typography.lineHeights.sm};
    column-gap: ${theme.ui.spacings.sm};
    padding: 0 ${theme.ui.spacings.md};
  `
}

const getVariantStyles = ({
  theme,
  variant,
}: PropsWithTheme<ButtonStyleProps>) => {
  const { base, hover } = ButtonColors[variant]

  return css`
    background-color: ${theme.ui.colors[base.background]};
    border: 1px solid ${theme.ui.colors[base.border]};
    color: ${theme.ui.colors[base.color]};

    &:hover {
      background-color: ${theme.ui.colors[hover.background]};
      border-color: ${theme.ui.colors[hover.border]};
      color: ${theme.ui.colors[hover.color]};
    }
  `
}

const StyledButton = styled.button<ButtonStyleProps>`
  // default value for size, is overriden with getSizeStyles
  --size: ${({ theme }) => theme.ui.spacings.xxl};

  ${getSizeStyles}
  ${getVariantStyles}

  height: var(--size);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
  flex-shrink: 0; // so flex cannot make button smaller than its content
  overflow: hidden;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.ui.typography.fontFamilies.default};
  font-weight: ${({ theme }) => theme.ui.typography.fontWeights.semiBold};
  border-radius: ${({ theme }) => theme.ui.spacings.sm};

  ${getShapeStyles}

  & svg {
    // height instead of width because of correct sizing when viewbox is smaller than 24px
    height: calc(var(--size) / 2);
    flex-shrink: 0;
  }

  &:focus,
  &:active {
    ${getDefaultOutline}
  }

  &:disabled {
    cursor: auto;
    user-select: none;
    background-color: ${({ theme }) => theme.ui.colors['navy.10']};
    border-color: ${({ theme }) => theme.ui.colors['navy.10']};
    color: ${({ theme }) => theme.ui.colors['navy.50']};
  }

  &:hover {
    transition: background-color 250ms ease-out, border-color 250ms ease-out;

    &:disabled {
      background-color: ${({ theme }) => theme.ui.colors['navy.10']};
      border-color: ${({ theme }) => theme.ui.colors['navy.10']};
      color: ${({ theme }) => theme.ui.colors['navy.50']};
    }
  }

  ${getCSSObjectFromStyleProps}
`

export interface ButtonProps
  extends Omit<ComponentProps<typeof StyledButton>, 'variant' | 'size'> {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: SvgIcon
  addon?: ReactNode
  addonIcon?: SvgIcon
}

export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        variant = 'default',
        size = 'default',
        shape = 'default',
        type = 'button',
        children,
        disabled,
        icon: Icon,
        addonIcon: AddonIcon,
        addon,
        onClick: onClickOriginal,
        ...props
      },
      ref,
    ) => {
      const isAllowedToClickDateRef = useRef<number | null>(null)

      const onClick = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
          const { current: lastClickDate } = isAllowedToClickDateRef
          const isAllowToClick =
            lastClickDate === null || Math.abs(Date.now() - lastClickDate) > 100
          if (isFunction(onClickOriginal) && isAllowToClick) {
            const result = onClickOriginal(e)
            isAllowedToClickDateRef.current = Date.now()
            return result
          }
        },
        [onClickOriginal],
      )

      return (
        <StyledButton
          {...props}
          {...{
            ref,
            size,
            disabled,
            variant,
            shape,
            onClick,
            type,
          }}>
          {Icon ? <Icon /> : null}
          {children}
          {AddonIcon ? <AddonIcon /> : null}
          {addon ? addon : null}
        </StyledButton>
      )
    },
  ),
)
